import React, { useCallback, useEffect, useRef, useState } from 'react';
import useSyncedZoom, { ZoomIndicator } from '../firstLastImageCompare/useSyncedZoom';
import { sigmaBase } from './medirGeom';

// La foto 4K con zoom/paneo (mismo hook que Antes y Ahora / Rayos X) y encima un SVG con lo que se marca.
// Todo lo que entra y sale está en píxeles de la foto ORIGINAL (sin rotar): la rotación de la cámara
// (orientation 90/180/270) sólo cambia cómo se dibuja.
//  - Tocar/clic en la foto: onAgregar([u, v, σ]) — σ = precisión del clic en px de la foto (depende del zoom).
//  - Arrastrar un punto editable: onMover(ref, [u, v, σ]). Mientras se arrastra aparece una lupa.
// capas: [{ tipo: 'linea', pts, cerrada, color, ancho, guiones, opacidad, relleno, opRelleno }
//         { tipo: 'punto', p, color, etiqueta, ref (si se puede arrastrar), forma: 'cuadrado' }
//         { tipo: 'texto', p, texto, color, ancla, lejosDe (punto del que se aleja), sep }
//         { tipo: 'etiqueta', p, texto, sub (texto chico, el ±), color }]  pastilla con el número sobre la línea
// onPuntero(p | null): dónde está el mouse (vista previa en vivo)

const esTactil = () => typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

const MATRIZ = {
  0: () => [1, 0, 0, 1, 0, 0],
  90: (w, h) => [0, 1, -1, 0, h, 0],
  180: (w, h) => [-1, 0, 0, -1, w, h],
  270: (w, h) => [0, -1, 1, 0, 0, w],
};
export const aPantalla = (rot, w, h, p) => {
  const [a, b, c, d, e, f] = (MATRIZ[rot] || MATRIZ[0])(w, h);
  return [a * p[0] + c * p[1] + e, b * p[0] + d * p[1] + f];
};
export const deDisplay = (rot, w, h, q) => {
  if (rot === 90) return [q[1], h - q[0]];
  if (rot === 180) return [w - q[0], h - q[1]];
  if (rot === 270) return [w - q[1], q[0]];
  return [q[0], q[1]];
};

const MedirFoto = ({ url, w, h, rot = 0, capas = [], onAgregar, onMover, onPuntero, cursor = 'crosshair', children }) => {
  const girada = rot === 90 || rot === 270;
  const Wd = girada ? h : w;
  const Hd = girada ? w : h;
  const [z, setZ] = useState(1);
  const zRef = useRef(1);
  const vpRef = useRef(null);
  const [vpW, setVpW] = useState(800);
  const zoom = useSyncedZoom({
    maxScale: 14,
    onChange: (st) => {
      zRef.current = st.s;
      if (Math.abs(st.s - z) / z > 0.04 || (st.s === 1 && z !== 1)) setZ(st.s);
    },
  });
  const setViewport = useCallback((el) => {
    vpRef.current = el;
    zoom.viewportRef(el);
  }, [zoom.viewportRef]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = vpRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setVpW(el.clientWidth || 800));
    ro.observe(el);
    setVpW(el.clientWidth || 800);
    return () => ro.disconnect();
  }, []);

  // unidades de la foto por píxel de pantalla con el zoom actual
  const k = Wd / Math.max(1, vpW) / z;
  const sigmaClic = () => Math.max(sigmaBase(w), (Wd / Math.max(1, vpRef.current?.clientWidth || vpW) / zRef.current) * 1.0);
  const aFoto = useCallback((clientX, clientY) => {
    const st = zoom.toStage(clientX, clientY);
    const el = vpRef.current;
    const sx = Wd / (el?.clientWidth || 1);
    const sy = Hd / (el?.clientHeight || 1);
    return deDisplay(rot, w, h, [st.x * sx, st.y * sy]);
  }, [zoom, Wd, Hd, rot, w, h]);

  // clic corto = punto nuevo (si no fue paneo ni pellizco)
  const gesto = useRef(null);
  const onPointerDown = (e) => {
    if (e.button > 0 || e.target.closest?.('[data-zoom-ignore]')) return;
    if (!gesto.current) gesto.current = { x: e.clientX, y: e.clientY, t: Date.now(), n: 0, max: 0 };
    gesto.current.n += 1;
    gesto.current.max = Math.max(gesto.current.max, gesto.current.n);
  };
  const onPointerUp = (e) => {
    const g = gesto.current;
    if (!g) return;
    g.n -= 1;
    if (g.n > 0) return;
    gesto.current = null;
    if (g.max > 1 || Math.hypot(e.clientX - g.x, e.clientY - g.y) > 6 || Date.now() - g.t > 900) return;
    if (!onAgregar) return;
    const p = aFoto(e.clientX, e.clientY);
    if (p[0] < 0 || p[1] < 0 || p[0] > w || p[1] > h) return;
    onAgregar([p[0], p[1], sigmaClic()]);
  };

  // arrastre de puntos
  const [arrastre, setArrastre] = useState(null); // { ref, p }
  const empezarArrastre = (ref) => (e) => {
    if (!onMover) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setArrastre({ ref, p: aFoto(e.clientX, e.clientY) });
  };
  const moverArrastre = (e) => {
    if (!arrastre) return;
    const p = aFoto(e.clientX, e.clientY);
    const q = [Math.min(w, Math.max(0, p[0])), Math.min(h, Math.max(0, p[1]))];
    setArrastre((a) => (a ? { ...a, p: q } : a));
    onMover(arrastre.ref, [q[0], q[1], sigmaClic()]);
  };
  const terminarArrastre = () => setArrastre(null);

  const tactil = esTactil();
  const rPunto = (tactil ? 11 : 7) * k;
  const D = (p) => aPantalla(rot, w, h, p);
  const pathDe = (pts, cerrada) => pts.map((p, i) => `${i ? 'L' : 'M'}${D(p)[0].toFixed(1)},${D(p)[1].toFixed(1)}`).join(' ') + (cerrada ? ' Z' : '');
  const matriz = (MATRIZ[rot] || MATRIZ[0])(w, h).join(',');

  const dibujo = (conHandles) => capas.map((c, i) => {
    if (c.tipo === 'linea' && c.pts?.length >= 2) {
      return (
        <path
          key={i}
          d={pathDe(c.pts, c.cerrada)}
          fill={c.relleno || 'none'}
          fillOpacity={c.relleno ? (c.opRelleno ?? 0.18) : 0}
          stroke={c.color || '#2563eb'}
          strokeOpacity={c.opacidad ?? 1}
          strokeWidth={(c.ancho || 2.5) * k}
          strokeDasharray={c.guiones ? `${8 * k} ${6 * k}` : undefined}
          strokeLinejoin="round"
          strokeLinecap="round"
          pointerEvents="none"
        />
      );
    }
    if (c.tipo === 'punto') {
      const [x, y] = D(c.p);
      const r = c.chico ? rPunto * 0.6 : rPunto;
      const forma = c.forma === 'cuadrado'
        ? <rect x={x - r} y={y - r} width={2 * r} height={2 * r} rx={2 * k} />
        : <circle cx={x} cy={y} r={r} />;
      return (
        <g
          key={i}
          fill="#ffffff"
          fillOpacity={0.35}
          stroke={c.color || '#2563eb'}
          strokeWidth={2.2 * k}
          style={{ cursor: c.ref && conHandles ? 'move' : 'inherit', touchAction: 'none' }}
          {...(c.ref && conHandles ? { 'data-zoom-ignore': true, onPointerDown: empezarArrastre(c.ref), onPointerMove: moverArrastre, onPointerUp: terminarArrastre, onPointerCancel: terminarArrastre } : { pointerEvents: 'none' })}
        >
          {forma}
          <circle cx={x} cy={y} r={1.3 * k} fill={c.color || '#2563eb'} fillOpacity={1} stroke="none" />
          {c.etiqueta != null && (
            <text x={x + r + 3 * k} y={y - r - 2 * k} fontSize={12 * k} fontWeight="700" fill={c.color || '#2563eb'} stroke="#fff" strokeWidth={3 * k} paintOrder="stroke" pointerEvents="none">
              {c.etiqueta}
            </text>
          )}
        </g>
      );
    }
    if (c.tipo === 'etiqueta' && c.p) {
      // el número sobre la línea: una pastilla que no tapa el dibujo
      const [x, y] = D(c.p);
      const t1 = String(c.texto || '');
      const t2 = c.sub ? String(c.sub) : '';
      const fs = (c.tam || 15) * k;
      const anchoTxt = (t1.length * 0.62 + (t2 ? t2.length * 0.52 + 0.5 : 0)) * fs + 18 * k;
      const alto = fs * 1.7;
      const oy = (c.arriba ?? 22) * k;
      // al costado (alturas): la pastilla a la derecha de la línea, para no tapar los puntos
      const ox = c.costado ? anchoTxt / 2 + 14 * k : 0;
      return (
        <g key={i} pointerEvents="none" transform={`translate(${x + ox} ${y - oy})`}>
          <rect x={-anchoTxt / 2} y={-alto / 2} width={anchoTxt} height={alto} rx={alto / 2} fill={c.color || '#16a34a'} stroke="#fff" strokeWidth={1.5 * k} />
          <text x={0} y={fs * 0.35} textAnchor="middle" fontSize={fs} fontWeight="800" fill="#fff" textRendering="geometricPrecision">
            {t1}{t2 && <tspan fontSize={fs * 0.78} fontWeight="600" dx={fs * 0.3}>{t2}</tspan>}
          </text>
        </g>
      );
    }
    if (c.tipo === 'texto' && c.p) {
      let [x, y] = D(c.p);
      if (c.lejosDe) {
        // correrlo hacia afuera (en píxeles de pantalla) para que no tape la figura ni choque con otros textos
        const [ox, oy] = D(c.lejosDe);
        const n = Math.hypot(x - ox, y - oy) || 1;
        x += ((x - ox) / n) * (c.sep || 22) * k;
        y += ((y - oy) / n) * (c.sep || 22) * k + 5 * k;
      }
      return (
        <text key={i} x={x} y={y} fontSize={(c.tam || 14) * k} fontWeight="700" fill={c.color || '#111827'} stroke="#fff" strokeWidth={4 * k} paintOrder="stroke" textAnchor={c.ancla || 'start'} pointerEvents="none">
          {c.texto}
        </text>
      );
    }
    return null;
  });

  // lupa (sobre todo para el dedo, que tapa el punto)
  const lupa = arrastre && (() => {
    const [x, y] = D(arrastre.p);
    const tam = 128;
    const span = (tam / 4) * k; // 4× del zoom actual
    return (
      <div className="pointer-events-none absolute top-2 left-2 z-30 rounded-full overflow-hidden border-2 border-white shadow-lg bg-gray-900" style={{ width: tam, height: tam }}>
        <svg width={tam} height={tam} viewBox={`${x - span / 2} ${y - span / 2} ${span} ${span}`}>
          <g transform={`matrix(${matriz})`}><image href={url} width={w} height={h} /></g>
          <line x1={x - span} y1={y} x2={x + span} y2={y} stroke="#ef4444" strokeWidth={span / 128} />
          <line x1={x} y1={y - span} x2={x} y2={y + span} stroke="#ef4444" strokeWidth={span / 128} />
        </svg>
      </div>
    );
  })();

  return (
    <div className="w-full flex justify-center">
      <div
        ref={setViewport}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerMove={(e) => {
          // vista previa con el mouse (no con el dedo): dónde caería el próximo punto
          if (!onPuntero || e.pointerType !== 'mouse' || e.buttons || arrastre) return;
          const p = aFoto(e.clientX, e.clientY);
          onPuntero(p[0] >= 0 && p[1] >= 0 && p[0] <= w && p[1] <= h ? [p[0], p[1], sigmaClic()] : null);
        }}
        onPointerLeave={() => onPuntero && onPuntero(null)}
        onPointerCancel={() => { gesto.current = null; }}
        className="relative w-full overflow-hidden rounded-lg bg-gray-900 select-none outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        style={{ aspectRatio: `${Wd} / ${Hd}`, maxWidth: `calc(72vh * ${Wd / Hd})`, cursor }}
      >
        <div ref={zoom.stageRef} className="absolute inset-0" style={{ transformOrigin: '0 0' }}>
          <svg viewBox={`0 0 ${Wd} ${Hd}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
            <g transform={`matrix(${matriz})`}>
              <image href={url} width={w} height={h} />
            </g>
            {dibujo(true)}
          </svg>
        </div>
        {lupa}
        <ZoomIndicator ref={zoom.indicatorRef} onReset={() => zoom.reset()} />
        {children}
      </div>
    </div>
  );
};

export default MedirFoto;
